/**
 * Client for the PUBLIC token-public edge function (the /t/:code page).
 * Calls token-public (verify_jwt=false) via raw fetch with the anon apikey — the page is public and the
 * visitor is unauthenticated. The edge fn rate-limits, derives the actor server-side (3-factor) for
 * identity-bound behaviors, and delegates to the service-role redeem-token fn.
 */

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/token-public`;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export interface TokenBehavior {
  behavior_id: string;
  type: string;
  label: string;
  requires_identity: boolean;
}

export interface TokenResolve {
  found: boolean;
  status?: string;
  event_id?: string | null;
  is_bound?: boolean;
  available_behaviors: TokenBehavior[];
}

export interface TokenRedeemResult {
  redeemed?: boolean;
  fulfilled?: boolean;
  behavior_type?: string | null;
  /** Present for mint_reward_voucher — the minted voucher to show the (identity-proven) holder. */
  rewardVoucherCode?: string | null;
  error?: string;
  retryable?: boolean;
  status: number;
}

async function call(
  payload: Record<string, unknown>,
): Promise<{ body: Record<string, unknown>; status: number }> {
  const res = await fetch(FN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: ANON,
      Authorization: `Bearer ${ANON}`,
    },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  return { body, status: res.status };
}

/** Resolve a token's currently-available actions (minimal projection; no PII). */
export async function resolveToken(code: string): Promise<TokenResolve> {
  const { body } = await call({ action: "resolve", code });
  return {
    found: !!body?.found,
    status: body?.status as string | undefined,
    event_id: (body?.event_id as string | null) ?? null,
    is_bound: !!body?.is_bound,
    available_behaviors: (body?.available_behaviors as TokenBehavior[]) ?? [],
  };
}

export interface TokenRedeemInput {
  code: string;
  behaviorId: string;
  /** Stable across retries of the SAME attempt (F9 contract). */
  idempotencyKey: string;
  /** Required only for identity-bound (requires_identity) behaviors. */
  identity?: {
    email?: string;
    memberNumber?: string;
    lastName?: string;
    zip?: string;
  };
}

/** Redeem one behavior. Caller must pass a stable idempotencyKey per attempt (reuse on retry). */
export async function redeemToken(
  input: TokenRedeemInput,
): Promise<TokenRedeemResult> {
  const payload: Record<string, unknown> = {
    action: "redeem",
    code: input.code,
    behavior_id: input.behaviorId,
    idempotency_key: input.idempotencyKey,
  };
  if (input.identity) {
    if (input.identity.email) {
      payload.email = input.identity.email;
    }
    if (input.identity.memberNumber) {
      payload.memberNumber = input.identity.memberNumber;
    }
    if (input.identity.lastName) {
      payload.lastName = input.identity.lastName;
    }
    if (input.identity.zip) {
      payload.zip = input.identity.zip;
    }
  }
  const { body, status } = await call(payload);
  return {
    redeemed: !!body?.redeemed,
    fulfilled: !!body?.fulfilled,
    behavior_type: (body?.behavior_type as string | null) ?? null,
    rewardVoucherCode: (body?.reward_voucher_code as string | null) ?? null,
    error: body?.error as string | undefined,
    retryable: !!body?.retryable,
    status,
  };
}
